import React from 'react';
import {
  Typography,
  Box,
  MenuItem,
  Divider,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  TextField,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { Table, Card, CardContent, PageShell, PageHeader, PageBlockTitle, PageBlockHeader, TableActionsMenu, TableActionsMenuButton, destructiveMenuItemSx, stopTableRowClick } from '../ui';
import { acme, acmePageSectionCardSx, acmeBillingSectionContentSx } from '../../theme/acmeTheme';
import CampaignEmptyState from './CampaignEmptyState';
import CampaignListToolbar from './CampaignListToolbar';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import {
  capitalizeStatus,
  getEmailCampaignStatusIcon,
  getPercentage,
  getSmsCampaignStatusIcon,
} from '../../utils/campaignDashboardUtils';

const CampaignListDashboard = ({
  list,
  headerExtra = null,
  AnalyticsDialog,
  embedded = false,
  showSnackbars = true,
  PageSnackbars,
  snackbarProps,
}) => {
  const {
    config,
    campaigns,
    loading,
    viewMode,
    setViewMode,
    filters,
    setFilters,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    menuAnchor,
    selectedCampaign,
    openAnalyticsDialog,
    setOpenAnalyticsDialog,
    selectedCampaigns,
    openDeleteConfirmDialog,
    setOpenDeleteConfirmDialog,
    filteredCampaigns,
    deleteCount,
    handleRowClick,
    handleMenuOpen,
    handleMenuClose,
    toggleCampaignSelection,
    handleSelectAllOnPage,
    handleDeleteMenuClick,
    handleConfirmDelete,
    exportCampaigns,
    getStatus,
  } = list;

  const isSms = config.channel === 'sms';
  const StatusIcon = isSms ? getSmsCampaignStatusIcon : getEmailCampaignStatusIcon;
  const colSpan = config.columns.length + 2;

  const renderTableView = () => {
    const filteredOnPage = filteredCampaigns.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
    const numSelected = selectedCampaigns.filter((id) =>
      filteredOnPage.some((c) => c._id === id),
    ).length;
    const rowCount = filteredOnPage.length;

    return (
      <Table containerSx={{ boxShadow: 'none' }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={(e) => handleSelectAllOnPage(filteredOnPage, e.target.checked)}
                  inputProps={{ 'aria-label': 'select all campaigns on this page' }}
                />
              </TableCell>
              {config.columns.map((col) => (
                <TableCell key={col.id} sx={{ fontWeight: 'bold' }}>
                  {col.label}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
            <TableRow>
              <TableCell />
              {config.columns.map((col) => (
                <TableCell key={col.id}>
                  <TextField
                    value={filters[col.filterKey] ?? ''}
                    onChange={(e) =>
                      setFilters({ ...filters, [col.filterKey]: e.target.value })
                    }
                    placeholder="Filter"
                    variant="standard"
                  />
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOnPage.length === 0 ? (
              <CampaignEmptyState channel={config.emptyChannel} colSpan={colSpan} />
            ) : (
              filteredOnPage.map((campaign) => {
                const status = getStatus(campaign);
                const statusIcon = StatusIcon(status);
                return (
                  <TableRow
                    key={campaign._id}
                    onClick={() => handleRowClick(campaign._id)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: selectedCampaigns.includes(campaign._id)
                        ? acme.colors.brandMuted
                        : 'inherit',
                      '&:hover': { backgroundColor: acme.colors.muted },
                    }}
                  >
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCampaignSelection(campaign._id);
                      }}
                    >
                      <Checkbox checked={selectedCampaigns.includes(campaign._id)} />
                    </TableCell>
                    {config.columns.map((col) => {
                      if (col.isStatus) {
                        return (
                          <TableCell key={col.id}>
                            <Box display="flex" alignItems="center">
                              {statusIcon}
                              {capitalizeStatus(status)}
                            </Box>
                          </TableCell>
                        );
                      }
                      const value = config.getCellValue(campaign, col.id);
                      const total = col.withPercent
                        ? config.getPercentTotal(campaign, col.id)
                        : 0;
                      return (
                        <TableCell key={col.id}>
                          {col.withPercent
                            ? `${value} (${getPercentage(value, total)})`
                            : value}
                        </TableCell>
                      );
                    })}
                    <TableCell onClick={stopTableRowClick}>
                      <TableActionsMenuButton
                        ariaLabel="Campaign actions"
                        onClick={(e) => handleMenuOpen(e, campaign._id)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={filteredCampaigns.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[20, 50, 100]}
                labelRowsPerPage="Rows per page"
                colSpan={colSpan}
              />
            </TableRow>
          </TableFooter>
      </Table>
    );
  };

  const renderVisualView = () => {
    if (campaigns.length === 0) {
      return <CampaignEmptyState channel={config.emptyChannel} />;
    }

    return campaigns.map((campaign) => {
      const status = getStatus(campaign);
      const statusIcon = StatusIcon(status);
      const subtitle = config.getSubtitle?.(campaign);

      return (
        <Box
          key={campaign._id}
          onClick={() => handleRowClick(campaign._id)}
          sx={{
            border: `1px solid ${acme.colors.border}`,
            borderRadius: acme.radius.md,
            boxShadow: 'none',
            padding: '16px',
            marginBottom: '16px',
            cursor: 'pointer',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {campaign.campaignName || 'No Name'}
              </Typography>
              <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
                {capitalizeStatus(status)} — {config.getDateLabel(campaign)}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Box display="flex" alignItems="center">
              {statusIcon}
              <Typography variant="subtitle2">{capitalizeStatus(status)}</Typography>
            </Box>
          </Box>
          <Divider sx={{ marginY: '8px' }} />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {config.visualStats.map((stat) => (
              <Box key={stat.id} textAlign="center" flex={1}>
                <Typography variant="subtitle1">{stat.label}</Typography>
                <Typography variant="body1">
                  {config.getVisualStatValue(campaign, stat.id)}
                </Typography>
                <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
                  {config.getVisualStatPercent(campaign, stat)}
                </Typography>
              </Box>
            ))}
            <TableActionsMenuButton
              ariaLabel="Campaign actions"
              onClick={(e) => handleMenuOpen(e, campaign._id)}
            />
          </Box>
        </Box>
      );
    });
  };

  const body = (
    <>
      <PageBlockHeader>
        <PageBlockTitle>{config.title}</PageBlockTitle>
        <CampaignListToolbar
          inline
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          selectedCount={selectedCampaigns.length}
          onDeleteSelected={
            selectedCampaigns.length > 0
              ? () => setOpenDeleteConfirmDialog(true)
              : undefined
          }
          onExport={exportCampaigns}
        />
      </PageBlockHeader>
      {headerExtra}
      {loading ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={1.5}
          py={8}
        >
          <CircularProgress size={36} sx={{ color: acme.colors.brand }} />
          <Typography variant="body2" color="text.secondary">
            Loading campaigns…
          </Typography>
        </Box>
      ) : viewMode === 'table' ? (
        renderTableView()
      ) : (
        renderVisualView()
      )}
      {AnalyticsDialog && (
        <AnalyticsDialog
          campaign={selectedCampaign}
          open={openAnalyticsDialog}
          onClose={() => setOpenAnalyticsDialog(false)}
        />
      )}
      <ConfirmDeleteDialog
        open={openDeleteConfirmDialog}
        onClose={() => setOpenDeleteConfirmDialog(false)}
        onConfirm={handleConfirmDelete}
        count={deleteCount}
        itemLabel={config.deleteItemLabel}
      />
      <TableActionsMenu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={handleDeleteMenuClick} sx={destructiveMenuItemSx}>Delete</MenuItem>
      </TableActionsMenu>
      {showSnackbars && PageSnackbars && <PageSnackbars {...snackbarProps} />}
    </>
  );

  if (embedded) {
    return <Box sx={{ mt: 1 }}>{body}</Box>;
  }

  return (
    <PageShell>
      <PageHeader
        title={config.title}
        description={config.subtitle}
        sx={{ mb: 2 }}
      />
      <Card sx={{ ...acmePageSectionCardSx, boxShadow: 'none', mt: 0 }}>
        <CardContent sx={acmeBillingSectionContentSx}>{body}</CardContent>
      </Card>
    </PageShell>
  );
};

export default CampaignListDashboard;
